import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import type { MessageKey } from "../../i18n/dictionaries";
import {
  CORE_LINE_CODES,
  lineLabelKey,
  useLeadContext,
  usePrepareOffers,
  type LineCode,
} from "./use-prepare-offers";
import { PackagePicker } from "./package-picker";
import { ReassignDialog } from "./reassign-dialog";

const MAX_OFFERS = 3;
// Mirrors packages/validation premiumSchema: a non-negative decimal, ≤12 integer
// digits, ≤2 fractional — so ">= 0" is enforced client-side too (no minus sign).
const PREMIUM_RE = /^\d{1,12}(\.\d{1,2})?$/;

interface OfferDraft {
  packageCode: string;
  grossPremium: string;
}

const emptyOffer = (): OfferDraft => ({ packageCode: "", grossPremium: "" });
const premiumOk = (v: string): boolean => PREMIUM_RE.test(v.trim());

/** A best-effort Turkish gloss for the prepare-offers ProblemJson details so the
 *  advisor gets a human message; the RAW detail is always shown beneath it (the
 *  console branches on `code`, but these domain details are the useful signal). */
function glossKey(detail: string): MessageKey | null {
  const d = detail.toLowerCase();
  if (d.includes("line-indeterminate")) return "po.err.lineNeeded";
  if (d.includes("asset-ambiguous")) return "po.err.assetAmbiguous";
  if (d.includes("no-insurable-asset") || d.includes("has-no-insurable-asset"))
    return "po.err.noAsset";
  if (d.includes("only-trafik-kasko")) return "po.err.unsupportedLine";
  if (d.includes("not permitted") || d.includes("not-authorized")) return "po.err.notAuthorized";
  if (d.includes("premium")) return "po.err.premium";
  if (d.includes("package")) return "po.err.package";
  return null;
}

// PREPARE-OFFERS — open a web lead from the daily queue and author 1–3 offers →
// one call mints a scored DRAFT recommendation; on success we hand off to the
// EXISTING recommendation detail (choose → tier-2 → present). No new flow.
export function PrepareOffersPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const { leadId = "" } = useParams();
  const location = useLocation();
  const { me } = usePrincipal();
  // The glance context comes from the lead-context READ (GET /v1/leads/:id/context
  // → name/plate/phone), preferred over the queue's route-state hand-off (which is
  // absent when the surface is opened directly). If the read fails we fall back to
  // the nav-state name/plate; phone is only ever shown from the read (never faked).
  const navCtx = (location.state ?? {}) as { partyName?: string | null; plate?: string | null };
  const leadCtx = useLeadContext(leadId);
  const read = leadCtx.data;
  const ctxPartyName = read ? read.partyName : (navCtx.partyName ?? null);
  const ctxPlate = read ? read.plate : (navCtx.plate ?? null);
  const ctxPhone = read?.phone ?? null;
  const prepare = usePrepareOffers(leadId);
  const canReassign = hasGrant(me, "lead:assign", "own");

  const [lineCode, setLineCode] = useState<LineCode>("trafik");
  const [offers, setOffers] = useState<OfferDraft[]>([emptyOffer()]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [newOwner, setNewOwner] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const patch = (i: number, next: Partial<OfferDraft>) =>
    setOffers((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...next } : o)));
  const addRow = () =>
    setOffers((prev) => (prev.length < MAX_OFFERS ? [...prev, emptyOffer()] : prev));
  const removeRow = (i: number) =>
    setOffers((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  // Submittable = every row the advisor started (a package code entered); each
  // such row must carry a valid TRY premium. At least one is required.
  const started = offers.filter((o) => o.packageCode.trim() !== "");
  const canSubmit =
    started.length >= 1 && started.every((o) => premiumOk(o.grossPremium)) && !prepare.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    prepare.mutate(
      {
        lineCode,
        offers: started.map((o) => ({
          packageCode: o.packageCode.trim(),
          grossPremium: o.grossPremium.trim(),
          currency: "TRY" as const,
        })),
      },
      { onSuccess: (res) => navigate(`/recommendations/${res.recommendationId}`) },
    );
  };

  const problem = prepare.error instanceof ProblemError ? prepare.error.problem : null;
  const glossed = problem ? glossKey(problem.detail) : null;

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("po.heading")}</h1>
          <p className="surface__subtitle">{t("po.subheading")}</p>
        </div>
        {canReassign && (
          <div className="surface__actions">
            <button
              type="button"
              className="k-btn k-btn--secondary k-btn--sm"
              data-testid="po-reassign-open"
              onClick={() => setReassignOpen(true)}
            >
              <Icon name="member" size={15} />
              {t("po.reassign.open")}
            </button>
          </div>
        )}
      </header>

      {/* CONTEXT HEADER — who this offer is for. Name + plate + PHONE come from the
          lead-context read (GET /v1/leads/:id/context), falling back to the queue's
          route-state name/plate if the read fails. Each field is nullable — a chip
          renders only when its value is present (no fabricated phone). */}
      <div
        className="k-card k-card--pad-sm"
        data-testid="po-context"
        style={{ maxWidth: "42rem", marginBottom: 16 }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span className="k-chip">
            <Icon name="member" size={15} />
            {ctxPartyName || t("po.contextUnknownName")}
          </span>
          {ctxPlate && (
            <span className="k-chip">
              {t("leads.plate")}: {ctxPlate}
            </span>
          )}
          {ctxPhone && (
            <span className="k-chip" data-testid="po-context-phone">
              <Icon name="conversation" size={15} />
              {ctxPhone}
            </span>
          )}
          {newOwner && (
            <span className="k-chip" data-testid="po-new-owner">
              <Icon name="check-circle" size={15} />
              {t("po.reassign.owner")}: {newOwner}
            </span>
          )}
        </div>
      </div>

      <form className="po-form k-card" onSubmit={submit} style={{ maxWidth: "42rem" }}>
        <label className="k-field">
          <span className="k-field__label">{t("po.line")}</span>
          <span className="k-select">
            <select
              className="k-select__native"
              aria-label={t("po.line")}
              value={lineCode}
              onChange={(e) => setLineCode(e.target.value as LineCode)}
            >
              {CORE_LINE_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(lineLabelKey(code))}
                </option>
              ))}
            </select>
            <span className="k-select__chevron">
              <Icon name="chevron-down" size={16} />
            </span>
          </span>
        </label>

        <div className="po-offers">
          {offers.map((o, i) => {
            const premiumInvalid = o.grossPremium.trim() !== "" && !premiumOk(o.grossPremium);
            return (
              <div className="po-offer-row" data-testid="po-offer-row" key={i}>
                <span className="k-counter po-row-index">{i + 1}</span>
                <label className="k-field po-package">
                  <span className="k-field__label">{t("po.packageCode")}</span>
                  <PackagePicker
                    lineCode={lineCode}
                    value={o.packageCode}
                    ariaLabel={`${t("po.packageCode")} ${i + 1}`}
                    disabled={prepare.isPending}
                    onChange={(packageCode) => patch(i, { packageCode })}
                  />
                </label>
                <label className="k-field po-premium">
                  <span className="k-field__label">{t("po.premium")}</span>
                  <input
                    className={`k-input${premiumInvalid ? " k-input--error" : ""}`}
                    aria-label={`${t("po.premium")} ${i + 1}`}
                    inputMode="decimal"
                    value={o.grossPremium}
                    placeholder="0.00"
                    aria-invalid={premiumInvalid}
                    onChange={(e) => patch(i, { grossPremium: e.target.value })}
                  />
                  {premiumInvalid && (
                    <span role="alert" className="k-field__error">
                      <Icon name="alert-triangle" size={14} />
                      {t("po.premiumInvalid")}
                    </span>
                  )}
                </label>
                {offers.length > 1 && (
                  <button
                    type="button"
                    className="k-btn k-btn--ghost k-btn--sm"
                    data-testid="po-remove"
                    disabled={prepare.isPending}
                    onClick={() => removeRow(i)}
                  >
                    <Icon name="trash" size={15} />
                    {t("po.removeOffer")}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="k-field__hint">{t("po.packageHint")}</p>

        <div className="po-actions">
          <button
            type="button"
            className="k-btn k-btn--secondary k-btn--sm"
            data-testid="po-add"
            disabled={offers.length >= MAX_OFFERS || prepare.isPending}
            onClick={addRow}
          >
            <Icon name="plus" size={15} />
            {t("po.addOffer")}
          </button>
          <button type="submit" className="k-btn k-btn--primary" disabled={!canSubmit}>
            <Icon name="arrow-right" size={16} />
            {prepare.isPending ? t("po.submitting") : t("po.submit")}
          </button>
        </div>

        {started.length === 0 && <p className="k-field__hint">{t("po.needOne")}</p>}

        {problem && (
          <p role="alert" className="k-field__error" data-testid="po-error">
            <Icon name="alert-triangle" size={14} />
            <span>
              {glossed ? t(glossed) : problem.detail}
              {glossed && <span className="k-field__hint"> — {problem.detail}</span>}
            </span>
          </p>
        )}
      </form>

      {reassignOpen && (
        <ReassignDialog
          leadId={leadId}
          onClose={() => setReassignOpen(false)}
          onReassigned={(advisor) => {
            setNewOwner(advisor.displayName);
            setToast(`${t("po.reassign.success")} ${advisor.displayName}`);
          }}
        />
      )}

      {toast && (
        <div
          className="k-toaststack"
          style={{ position: "fixed", right: 24, bottom: 24, zIndex: 60 }}
        >
          <div className="k-toast k-toast--success" role="status" data-testid="po-toast">
            <span className="k-toast__icon k-toast__icon--success">
              <Icon name="check-circle" size={18} />
            </span>
            <span className="k-toast__body">
              <span className="k-toast__title">{toast}</span>
            </span>
            <button
              type="button"
              className="k-toast__close"
              aria-label={t("po.reassign.cancel")}
              onClick={() => setToast(null)}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
